import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { Accordion, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '@feathr/components';

import type { IUploadResult } from '../creatives.types';

import * as styles from './UploadResultsList.css';

interface IUploadResultsListProps {
  /** Array of upload results to display */
  results: IUploadResult[];
}

/**
 * Displays a list of upload results showing successful and failed uploads.
 */
function UploadResultsList({ results }: Readonly<IUploadResultsListProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const successes = results.filter((r) => r.success);
  const failures = results.filter((r) => !r.success);

  const defaultOpen: string[] = [];
  if (successes.length > 0) {
    defaultOpen.push('successes');
  }
  if (failures.length > 0) {
    defaultOpen.push('failures');
  }

  return (
    <Accordion defaultValue={defaultOpen} multiple={true} variant={'separated'}>
      {successes.length > 0 && (
        <Accordion.Item value={'successes'}>
          <Accordion.Control>
            <Text variant={'smallSemibold'}>
              {t('Successful uploads ({{count}})', { count: successes.length })}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap={theme.spacing['2']}>
              {successes.map((result) => (
                <div className={styles.resultItem} key={result.filename}>
                  <Icon className={styles.successIcon} icon={faCircleCheck} />
                  <Text className={styles.filename} variant={'defaultRegular'}>
                    {result.filename}
                  </Text>
                </div>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      )}

      {failures.length > 0 && (
        <Accordion.Item value={'failures'}>
          <Accordion.Control>
            <Text variant={'smallSemibold'}>
              {t('Failed uploads ({{count}})', { count: failures.length })}
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap={theme.spacing['2']}>
              {failures.map((result) => (
                <div className={styles.resultItem} key={result.filename}>
                  <Icon className={styles.errorIcon} icon={faCircleXmark} />
                  <div className={styles.resultContent}>
                    <Text className={styles.filename} variant={'defaultRegular'}>
                      {result.filename}
                    </Text>
                    {result.error && (
                      <Text className={styles.errorMessage} variant={'smallRegular'}>
                        {result.error}
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      )}
    </Accordion>
  );
}

export default UploadResultsList;
